/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ЕДИНСТВЕННЫЙ МОДУЛЬ ПРИЛОЖЕНИЯ, КОТОРОМУ ПОЗВОЛЕНО ТРОГАТЬ WEB STORAGE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ни `lib/http.ts`, ни сторы, ни вью не обращаются к `localStorage` /
 * `sessionStorage` напрямую — и это не стилистика, а стратегия отхода:
 * переход на httpOnly-куки (§2.5 плана) должен означать переписывание
 * **одного этого файла**, при неизменных интерцепторе, сторах и вью.
 * Поэтому наружу торчат только глаголы («сохрани пару», «дай refresh»,
 * «вычисти»), но нигде — «положи вот в этот Storage вот под этим ключом».
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПОЧЕМУ ACCESS-ТОКЕН НИКОГДА НЕ ЛОЖИТСЯ НА ДИСК
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Access-токен — предъявительский и **неотзываемый на своём горизонте**:
 * сервер проверяет только подпись и `exp` (§1.4 плана), сверяясь с множеством
 * отозванных сессий, которое живёт в памяти процесса. Утёкший access даёт
 * полный доступ на все свои 15 минут, и отозвать его дороже, чем не отдавать.
 *
 * Цена того, что он лежит в модульной переменной: **один сетевой round-trip
 * на загрузку страницы** — холодный старт не находит access и делает
 * `POST /auth/refresh` (это и есть `auth.bootstrap()`, §2.6). Один запрос
 * за то, чтобы XSS не выносил готовый ключ с диска, — сделка очевидная.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПОЧЕМУ REFRESH ЛЕЖИТ В РАЗНЫХ ХРАНИЛИЩАХ
 * ───────────────────────────────────────────────────────────────────────────
 *
 *   `rememberMe: true`  → `localStorage`   (сервер даёт 30 дней)
 *   `rememberMe: false` → `sessionStorage` (сервер даёт 12 часов)
 *
 * Это единственный способ сделать семантику честной. Сервер уже развёл сроки,
 * но `localStorage` переживает закрытие браузера **всегда** — и пользователь,
 * явно снявший галочку «запомнить меня», всё равно возобновил бы сессию,
 * открыв браузер утром. `sessionStorage` умирает вместе со вкладкой, то есть
 * ровно тогда, когда пользователь и просил.
 *
 * `getRefreshToken()` читает `localStorage ?? sessionStorage`, поэтому всему
 * остальному коду эта развилка не видна вообще.
 *
 * **Инвариант: токен никогда не лежит в обоих хранилищах одновременно.**
 * Каждая запись чистит второе хранилище. Иначе разлогин по `sessionStorage`
 * оставлял бы живой «призрак» в `localStorage`, и семантика галочки ломалась бы
 * молча — худший вид поломки.
 */

import type { TokenPair } from '@/types/api'

/**
 * Ключ намеренно с префиксом приложения: `localStorage` в браузере общий на
 * origin, а в dev на 5180 может оказаться не только эта панель.
 */
const REFRESH_KEY = 'scanner.admin.refresh_token'

/**
 * Access-токен. **Модульная переменная — это и есть хранилище.**
 * Живёт ровно столько, сколько живёт JS-контекст вкладки.
 */
let accessToken: string | null = null

/* ─────────────────────────────────────────────────────────────────────────
   Доступ к Storage — всегда через эти три обёртки
   ─────────────────────────────────────────────────────────────────────────
   Обращение к `localStorage` умеет **бросать**, а не возвращать null:
   Safari в приватном режиме, отключённые cookies, sandboxed iframe без
   `allow-same-origin`. Незащищённое `localStorage.getItem` в таком браузере
   роняет приложение белым экраном ещё до первого рендера, поэтому и геттер
   хранилища, и каждая операция обёрнуты. Деградация — «работаем как будто
   ничего не сохранено», а не падение. */

function storage(area: 'local' | 'session'): Storage | null {
  try {
    return area === 'local' ? globalThis.localStorage : globalThis.sessionStorage
  } catch {
    return null
  }
}

function read(area: 'local' | 'session'): string | null {
  try {
    return storage(area)?.getItem(REFRESH_KEY) ?? null
  } catch {
    return null
  }
}

function write(area: 'local' | 'session', value: string): void {
  try {
    storage(area)?.setItem(REFRESH_KEY, value)
  } catch {
    /* QuotaExceeded / приватный режим — молча остаёмся на in-memory access */
  }
}

function remove(area: 'local' | 'session'): void {
  try {
    storage(area)?.removeItem(REFRESH_KEY)
  } catch {
    /* см. write */
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   Публичная поверхность
   ───────────────────────────────────────────────────────────────────────── */

/** Access-токен для заголовка `Authorization`. `null` до логина и после холодного старта. */
function getAccessToken(): string | null {
  return accessToken
}

/**
 * Refresh-токен, где бы он ни лежал. Порядок `local ?? session` не случаен:
 * инвариант «не в обоих сразу» держит `save()`, а этот порядок делает чтение
 * корректным даже если инвариант кто-то нарушит — «запомнили» побеждает.
 */
function getRefreshToken(): string | null {
  return read('local') ?? read('session')
}

/**
 * Пользователь просил себя запомнить?
 *
 * Флаг **не хранится отдельно** — он выводится из того, в каком хранилище
 * лежит токен. Отдельный ключ был бы вторым источником правды и рано или
 * поздно разъехался бы с первым; здесь рассинхронизироваться нечему.
 */
function isRemembered(): boolean {
  return read('local') !== null
}

/**
 * Кладёт пару токенов.
 *
 * @param rememberMe
 *   • **передан** (логин) — выбирает хранилище: `true` → `localStorage`, `false` → `sessionStorage`;
 *   • **опущен** (ротация после `POST /auth/refresh` или смены пароля) — хранилище
 *     сохраняется тем же, каким его выбрал логин.
 *
 * Второй случай — не мелочь: сервер фиксирует `remember_me` на сессии при логине
 * и не меняет его при ротации (§1.4 плана). Если бы клиент при каждой ротации
 * молча переезжал в `localStorage`, 12-часовая сессия начала бы переживать
 * закрытие браузера — клиент врал бы про то, что уже решил сервер.
 */
function save(tokens: TokenPair, rememberMe?: boolean): void {
  accessToken = tokens.accessToken

  const remember = rememberMe ?? isRemembered()
  const target = remember ? 'local' : 'session'
  const other = remember ? 'session' : 'local'

  write(target, tokens.refreshToken)
  // Держит инвариант «не в обоих сразу» — в том числе при смене галочки
  // между двумя логинами в одной вкладке.
  remove(other)
}

/** Полная зачистка: выход, `SESSION_REVOKED`, провалившийся refresh. */
function clear(): void {
  accessToken = null
  remove('local')
  remove('session')
}

/**
 * Кросс-вкладочный выход.
 *
 * Событие `storage` прилетает **только в другие вкладки** того же origin —
 * не в ту, что писала. Ровно это и нужно: вкладка A нажала «Выйти», вкладка B
 * узнаёт об этом, не опрашивая ничего.
 *
 * Три условия фильтра, каждое обязательное:
 *   • `storageArea === localStorage` — `sessionStorage` вообще не пересекает
 *     границу вкладки, а чужие ключи на этом origin нас не касаются;
 *   • `key === null` (это `clear()`) **или** `key === REFRESH_KEY`;
 *   • `newValue === null` — иначе мы приняли бы за выход обычную **ротацию**
 *     токена в соседней вкладке и разлогинили бы пользователя на ровном месте.
 *
 * Возвращает функцию отписки — на случай HMR и тестов.
 */
function onExternalClear(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {}

  const handler = (event: StorageEvent): void => {
    const local = storage('local')
    if (local !== null && event.storageArea !== local) return
    if (event.key !== null && event.key !== REFRESH_KEY) return
    if (event.newValue !== null) return

    // Локальное зеркало обязано умереть вместе с диском: иначе эта вкладка
    // осталась бы с живым access-токеном на все свои 15 минут.
    accessToken = null
    cb()
  }

  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

/**
 * Единый объект — импортируется как `import { tokenStorage } from '@/lib/token-storage'`.
 * Одна точка входа вместо россыпи функций: `tokenStorage.clear()` на месте вызова
 * читается однозначно, а `clear()` — нет.
 */
export const tokenStorage = {
  getAccessToken,
  getRefreshToken,
  isRemembered,
  save,
  clear,
  onExternalClear,
}
